// ABOUTME: The breathing round state for 1A: the timeline into a round (petal fade, picker, sail fold), a running round, and the cumulative totals sent to the server.
// ABOUTME: The countdown and the swell length derive from the wall clock; state changes once a second, so the frame is not re-rendered more often.

import { useEffect, useRef, useState } from 'react';
import { breathDurAt } from '../../crossing/breathMath';
import { roundProgress } from '../../crossing/breathSky';

export type RoundPhase = 'petals' | 'entering' | 'picker' | 'closing' | 'starting' | 'rolling' | 'running' | 'stopped' | 'done';
export type RoundIntro = 'rest' | 'rolling' | 'round';
export type SailState = 'set' | 'furled';

// Breathing strategy (TRANS 1): the petals fade out over 0.5 s, then the picker fades in; the sky does not change.
const PETALS_FADE_MS = 500;
// Back from the picker: the options and the mask fade out over 0.7 s; to the petals, they mount hidden and come in 30 ms later so they fade.
const PICKER_FADE_MS = 700;
const PETALS_BACK_DELAY_MS = 30;
// A pick (TRANS 3): the options fade and the sails fold at 0.65 s; once the fold has finished the storm rolls in for 2.3 s, then the round starts.
const FOLD_START_MS = 650;
const ROLL_START_AFTER_FOLD_MS = 2500;
const FURLED_ROLL_START_MS = 700;
const ROLL_IN_MS = 2300;

export interface BreathTotals {
  breathSecondsTotal: number;
  breathRoundCount: number;
}

interface UseBreathingRoundArgs {
  seedSeconds: number;
  seedRounds: number;
  onRoundComplete: (totals: BreathTotals) => void;
  reducedMotion?: boolean; // every delay is 0 and the sails are furled at once
}

export interface BreathingRound {
  phase: RoundPhase;
  total: number; // seconds in the current round
  leftSec: number; // whole seconds left in the current round, 0 once done
  secondsTotal: number; // cumulative, including completed rounds and seconds folded in from interrupted ones
  roundCount: number; // completed rounds, cumulative
  unsentSeconds: boolean; // a round is running, or seconds were folded in that no save has carried yet
  intro: RoundIntro; // the scene's lead-in: the resting sky, the new storm rolling in over it, then the round's own sky
  sails: SailState;
  sceneX: number; // the sky strip's position 0..1: 0 while the new storm rolls in, the round's progress while it runs, 1 once done
  previousX: number; // the strip that rolls out when a new storm rolls in: where the last round left the sky, 1 before any round
  hasRun: boolean; // a round has been started since the screen opened
  lastRoundDone: boolean; // the last round ran to 0:00
  closingTo: 'petals' | 'stopped' | 'done'; // where the picker is closing to (meaningful while phase is 'closing')
  petalsBack: boolean; // closing to the petals: false as the picker starts to fade, true a moment later, so the petals fade in
  closePicker: () => void; // Back from the picker: fades it out and returns to the petals, the stopped scene or the finished round
  openStrategy: () => void; // from the petals: fades them out, then the picker in; the sky stays as it is
  openPicker: () => void; // from a round: the picker and its mask at once over the current scene
  start: (secs: number) => void;
  furl: () => void;
  unfurl: () => void;
  totalsNow: () => BreathTotals; // includes the running round's elapsed whole seconds
  elapsedMs: () => number; // milliseconds into the running round, read from the wall clock; 0 outside a round
  swellDur: number; // the ramp's current swell length in seconds (3 outside a round), changing at most once a second
  roundKey: number; // changes with every start, so the cue starts on IN again
}

const TICK_MS = 100;

export function useBreathingRound({ seedSeconds, seedRounds, onRoundComplete, reducedMotion = false }: UseBreathingRoundArgs): BreathingRound {
  const [phase, setPhase] = useState<RoundPhase>('petals');
  const [total, setTotal] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [secondsTotal, setSecondsTotal] = useState(seedSeconds);
  const [roundCount, setRoundCount] = useState(seedRounds);
  const [sentSeconds, setSentSeconds] = useState(seedSeconds);
  const [roundKey, setRoundKey] = useState(0);
  const [sails, setSails] = useState<SailState>('set');
  const [hasRun, setHasRun] = useState(false);
  const [lastRoundDone, setLastRoundDone] = useState(false);
  const [closingTo, setClosingTo] = useState<'petals' | 'stopped' | 'done'>('petals');
  const [petalsBack, setPetalsBack] = useState(false);
  // The sky strip's position when the last round was left: the picker's mask shows over it and a new storm rolls it out.
  const [frozenX, setFrozenX] = useState(1);

  const startedAtRef = useRef(0);
  const totalRef = useRef(0);
  const completedRef = useRef(false);
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  // "Latest ref" pattern: the tick effect is created once per round, so it reads these instead of closing over them.
  const totalsRef = useRef({ secondsTotal, roundCount });
  const onRoundCompleteRef = useRef(onRoundComplete);
  useEffect(() => {
    totalsRef.current = { secondsTotal, roundCount };
    onRoundCompleteRef.current = onRoundComplete;
  });

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };
  const after = (ms: number, action: () => void) => {
    timersRef.current.push(setTimeout(action, ms));
  };
  useEffect(() => clearTimers, []);

  useEffect(() => {
    if (phase !== 'running') return;
    const id = setInterval(() => {
      const now = Date.now();
      const deadline = startedAtRef.current + totalRef.current * 1000;
      setElapsedSec(Math.min(totalRef.current, Math.floor((now - startedAtRef.current) / 1000)));
      if (now < deadline || completedRef.current) return;
      completedRef.current = true;
      const totals = {
        breathSecondsTotal: totalsRef.current.secondsTotal + totalRef.current,
        breathRoundCount: totalsRef.current.roundCount + 1,
      };
      setSecondsTotal(totals.breathSecondsTotal);
      setRoundCount(totals.breathRoundCount);
      setSentSeconds(totals.breathSecondsTotal);
      setLastRoundDone(true);
      setPhase('done');
      onRoundCompleteRef.current(totals);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [phase]);

  // Under reduced motion the sky does not move during the round: it stays on the storm until 0:00 shows the storm row.
  const sceneX = phase === 'running' ? (reducedMotion ? 0 : roundProgress(true, total, Math.max(0, total - elapsedSec)))
    : phase === 'done' ? 1
    : phase === 'rolling' ? 0
    : hasRun ? frozenX : 1;
  const previousX = hasRun ? frozenX : 1;
  const intro: RoundIntro = phase === 'rolling' ? 'rolling' : phase === 'running' || phase === 'stopped' || phase === 'done' ? 'round' : 'rest';

  // An interrupted round keeps the whole seconds actually breathed; the round count is for completed rounds only.
  const foldRunningRound = () => {
    if (phase === 'running') setSecondsTotal(seconds => seconds + elapsedSec);
  };

  const openStrategy = () => {
    if (phase !== 'petals') return;
    if (reducedMotion) {
      setPhase('picker');
      return;
    }
    clearTimers();
    setPhase('entering');
    after(PETALS_FADE_MS, () => setPhase('picker'));
  };

  // The restart: the mask over the scene as it stands, with no slide-in. A round's progress is read from the wall clock.
  const openPicker = () => {
    foldRunningRound();
    clearTimers();
    if (phase === 'running') setFrozenX(Math.min(1, elapsedMs() / (totalRef.current * 1000)));
    else if (phase === 'done') setFrozenX(1);
    setPhase('picker');
  };

  // Back: nothing is run or saved. Seconds folded in by Start over stay counted; the scene stays as it stood.
  const closePicker = () => {
    if (phase !== 'picker') return;
    const target = !hasRun ? 'petals' : lastRoundDone ? 'done' : 'stopped';
    if (reducedMotion) {
      setPhase(target);
      return;
    }
    clearTimers();
    setClosingTo(target);
    setPetalsBack(false);
    setPhase('closing');
    after(PICKER_FADE_MS, () => setPhase(target));
    if (target === 'petals') after(PETALS_BACK_DELAY_MS, () => setPetalsBack(true));
  };

  const begin = (secs: number) => {
    totalRef.current = secs;
    startedAtRef.current = Date.now();
    completedRef.current = false;
    setTotal(secs);
    setElapsedSec(0);
    setRoundKey(key => key + 1);
    setPhase('running');
  };

  const start = (secs: number) => {
    if (phase === 'starting' || phase === 'rolling') return;
    foldRunningRound();
    clearTimers();
    setHasRun(true);
    setLastRoundDone(false);
    if (reducedMotion) {
      setSails('furled');
      begin(secs);
      return;
    }
    setPhase('starting');
    const rollIn = () => {
      setPhase('rolling');
      after(ROLL_IN_MS, () => begin(secs));
    };
    if (sails === 'set') {
      after(FOLD_START_MS, () => setSails('furled'));
      after(ROLL_START_AFTER_FOLD_MS, rollIn);
    } else {
      after(FURLED_ROLL_START_MS, rollIn);
    }
  };

  const elapsedMs = () => {
    if (phase === 'running') return Math.min(Date.now() - startedAtRef.current, totalRef.current * 1000);
    return phase === 'done' ? totalRef.current * 1000 : 0;
  };

  // Whole seconds are enough: the ramp moves slowly, and a change only re-renders the frame once a second.
  const swellDur = phase === 'running' ? breathDurAt(elapsedSec, total) : phase === 'done' ? breathDurAt(total, total) : 3;

  const totalsNow = (): BreathTotals => ({
    breathSecondsTotal: secondsTotal + (phase === 'running' ? elapsedSec : 0),
    breathRoundCount: roundCount,
  });

  return {
    phase,
    total,
    leftSec: phase === 'running' ? Math.max(0, total - elapsedSec) : 0,
    secondsTotal,
    roundCount,
    unsentSeconds: phase === 'running' || secondsTotal > sentSeconds,
    intro,
    sails,
    sceneX,
    previousX,
    hasRun,
    lastRoundDone,
    closingTo,
    petalsBack,
    closePicker,
    openStrategy,
    openPicker,
    start,
    furl: () => setSails('furled'),
    unfurl: () => setSails('set'),
    totalsNow,
    elapsedMs,
    swellDur,
    roundKey,
  };
}
